import React, { useState, useEffect } from 'react'
import {
  CModal,
  CButton,
  CModalHeader,
  CModalBody,
  CFormInput,
  CForm,
  CModalFooter,
  CDatePicker,
} from '@coreui/react-pro'
import { useStore } from 'effector-react'
import {
  FilterEvent,
  $transactionsDetails,
  getTransactionsDetails,
} from './model'
import {
  TransactionsDetailsRequest,
  TransactionsDetailsItemFromServer,
} from '../TransactionsDetails/types'
import { RangeValue } from '../StatisticDetails/types'

type Props = {
  isOpen: boolean
  onClose: () => void
}

// Вспомогательная функция для скачивания CSV
const downloadCSV = (
  transactions: TransactionsDetailsItemFromServer[],
  fileName = 'transactions.csv',
) => {
  if (!transactions || transactions.length === 0) {
    console.warn('Нет данных для экспорта в CSV.')
    return
  }

  const headers = Object.keys(transactions[0])
  const csvRows = [
    headers.join(','),
    ...transactions.map((row) =>
      headers
        .map((fieldName) => {
          let value = (row as any)[fieldName]

          // null и undefined → пустая строка
          if (value === null || value === undefined) {
            value = ''
          }

          // массив → строка через ;
          if (Array.isArray(value)) {
            value = value.join(';')
          }

          let stringValue = String(value)

          // убираем переносы строк
          stringValue = stringValue.replace(/[\r\n]+/g, ' ')

          // экранируем и всегда оборачиваем в кавычки
          stringValue = `"${stringValue.replace(/"/g, '""')}"`

          return stringValue
        })
        .join(','),
    ),
  ]

  const csvString = csvRows.join('\n')
  const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)

  const link = document.createElement('a')
  link.setAttribute('href', url)
  link.setAttribute('download', fileName)
  link.style.visibility = 'hidden'
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}

export const FilterModal: React.FC<Props> = ({ isOpen, onClose }) => {
  const [currency, setCurrency] = useState('')
  const [dateTime, setDateTime] = useState<{
    dateFrom: Date | null
    dateTo: Date | null
  }>({
    dateFrom: null,
    dateTo: null,
  })

  const [isExporting, setIsExporting] = useState(false)
  const [exportPayload, setExportPayload] =
    useState<TransactionsDetailsRequest | null>(null)

  const currentTransactions = useStore($transactionsDetails)
  const isPending = useStore(getTransactionsDetails.pending)

  const handleDateChange = (
    date: Date | null,
    field: 'dateFrom' | 'dateTo',
  ) => {
    setDateTime((prev) => ({
      ...prev,
      [field]: date,
    }))
  }

  const buildFiltersPayload = (): TransactionsDetailsRequest => {
    const filters: TransactionsDetailsRequest['filters'] = {}
    if (currency) filters.currencies = [currency]
    if (dateTime.dateFrom || dateTime.dateTo) {
      filters.period = [dateTime.dateFrom, dateTime.dateTo] as RangeValue
    }
    return {
      limit: -1,
      page: 1,
      orderBy: 'createdAt',
      orderDirection: 'desc',
      searchText: '',
      filters,
    }
  }

  const handleExportAndDownload = () => {
    const payload = buildFiltersPayload()
    setIsExporting(true)
    setExportPayload(payload)
    FilterEvent(payload)
  }

  useEffect(() => {
    if (!exportPayload || isPending) return

    if (Array.isArray(currentTransactions) && currentTransactions.length > 0) {
      downloadCSV(
        currentTransactions,
        `transactions_export_${new Date().toISOString().slice(0, 10)}.csv`,
      )
    } else {
      console.warn('Нет транзакций для экспорта с указанными фильтрами.')
      alert('Нет транзакций для экспорта с указанными фильтрами.')
    }

    setExportPayload(null)
    setIsExporting(false)
    onClose()
  }, [exportPayload, isPending, currentTransactions, onClose])

  const handleCloseModal = () => {
    if (isExporting) {
      setExportPayload(null)
      setIsExporting(false)
    }
    onClose()
  }

  return (
    <CModal visible={isOpen} onClose={handleCloseModal}>
      <CModalHeader style={{ borderBottom: 'none' }}>
        <h4 className="text-center mb-4">Filters</h4>
      </CModalHeader>
      <CModalBody>
        <CForm>
          <CFormInput
            className="mb-2"
            label="Currency"
            value={currency}
            onChange={(e) => setCurrency(e.target.value)}
            disabled={isExporting}
          />
          <CDatePicker
            className="mb-2"
            label="From date"
            onDateChange={(date) => handleDateChange(date, 'dateFrom')}
            disabled={isExporting}
          />
          <CDatePicker
            className="mb-2"
            label="To date"
            onDateChange={(date) => handleDateChange(date, 'dateTo')}
            disabled={isExporting}
          />
        </CForm>
      </CModalBody>
      <CModalFooter className="d-flex flex-column">
        <CButton
          className="w-100"
          onClick={handleExportAndDownload}
          disabled={isExporting}
          color="success"
        >
          {isExporting ? 'Exporting...' : 'Export to CSV'}
        </CButton>
      </CModalFooter>
    </CModal>
  )
}
